import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import volunteers from "../Images/volunteers.png"; // Adjust the path as necessary
import { POST_FORM } from "../../../api/form";
import { Category } from "@mui/icons-material";

export default function Demo({ showSocialCause = true }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    organization: "",
    designation: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [apiSuccess, setApiSuccess] = useState("");

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.organization.trim())
      newErrors.organization = "Organization is required";
    if (!formData.designation.trim())
      newErrors.designation = "Designation is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setErrors((prev) => ({
      ...prev,
      [e.target.name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError(null);

    try {
      const res = await fetch(POST_FORM, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          category: "Social Cause Demo Form Org",
        }),
      });

      if (!res.ok) throw new Error("Failed to submit form");
      setApiSuccess("Thank you! We'll get in touch.");
      setSubmitted(true);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        organization: "",
        designation: "",
      });
    } catch (err) {
      console.error(err);
      setApiError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#fff", py: 6 }}>
      {/* Section: We Support Social Cause */}
      {showSocialCause && (
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            px: 3,
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            mb: 10,
          }}
        >
          {/* Left - Heading + Button */}
          <Box sx={{ flex: 1, mb: { xs: 6, lg: 0 } }}>
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
              Social Cause <br /> We Support
            </Typography>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#54b06f",
                "&:hover": { backgroundColor: "#16a34a" },
                color: "#fff",
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Join Social Cause
            </Button>
          </Box>

          {/* Center - Image */}
          <Box
            sx={{
              flex: 1,
              mx: 4,
              mb: { xs: 6, lg: 0 },
              textAlign: "center",
            }}
          >
            <img
              src={volunteers}
              alt="Volunteers"
              style={{ maxWidth: "100%", borderRadius: "12px" }}
            />
          </Box>

          {/* Right - Educate / Facilitate / Include */}
          <Box sx={{ flex: 1 }}>
            {[
              {
                title: "Educate",
                description:
                  "Educating people on the newer world with more intensive technology to help them onboard for what looks like the future.",
              },
              {
                title: "Facilitate",
                description:
                  "Collaborating to facilitate infrastructure of internet, education to provide access to resources for free in underprivileged zones.",
              },
              {
                title: "Include",
                description:
                  "Promoting and encouraging MSMEs, corporates and institutions to start initiatives to support the cause.",
              },
            ].map((item, i) => (
              <Box key={i} sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#6b21a8", fontWeight: "bold", mb: 1 }}
                >
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "#374151" }}>
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Section: Book A Demo Form */}
      <Box sx={{ px: !showSocialCause && 3, py: !showSocialCause && 8 }}>
        <Grid
          container
          spacing={4}
          maxWidth="lg"
          mx={showSocialCause ? "auto" : ""}
          alignItems="flex-start"
        >
          {/* Left Heading */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: "bold",
                color: "#000",
                lineHeight: 1.1,
                fontSize: { xs: "2.5rem", md: "3rem" },
              }}
            >
              {!showSocialCause ? "Request" : "Book"} A
              <br />
              Demo
            </Typography>
          </Grid>

          {/* Right Form */}
          <Grid item xs={12} md={8}>
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="firstName"
                    placeholder="First Name"
                    variant="outlined"
                    size="small"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={Boolean(errors.firstName)}
                    helperText={errors.firstName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="lastName"
                    placeholder="Last Name"
                    variant="outlined"
                    size="small"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={Boolean(errors.lastName)}
                    helperText={errors.lastName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="email"
                    placeholder="Organization Email ID"
                    variant="outlined"
                    size="small"
                    value={formData.email}
                    onChange={handleChange}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="organization"
                    placeholder="Organization"
                    variant="outlined"
                    size="small"
                    value={formData.organization}
                    onChange={handleChange}
                    error={Boolean(errors.organization)}
                    helperText={errors.organization}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="designation"
                    placeholder="Designation"
                    variant="outlined"
                    size="small"
                    value={formData.designation}
                    onChange={handleChange}
                    error={Boolean(errors.designation)}
                    helperText={errors.designation}
                  />
                </Grid>

                {apiError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{apiError}</Alert>
                  </Grid>
                )}
                {apiSuccess && (
                  <Grid item xs={12}>
                    <Alert severity="success">{apiSuccess}</Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    type="submit"
                    disabled={loading}
                    sx={{
                      backgroundColor: "#54b06f",
                      "&:hover": { backgroundColor: "#16a34a" },
                      color: "#000",
                      fontWeight: "bold",
                      height: "40px",
                      borderRadius: "8px",
                      textTransform: "none",
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
